import React, { useEffect, useRef, useState } from "react";
import { Send, Mic, X, Crosshair, ImagePlus, Loader2, Square } from "lucide-react";
import { useDepartmentStore } from "@/features/department";
import { usePickMode, usePickModeAvailable, useTogglePickMode } from "@/features/department";
import { useAutoResizeTextarea } from "@/hooks/useAutoResizeTextarea";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { detectRtl } from "@/shared/lib/rtl";
import { CHAT_MEDIA_UPLOAD_ACCEPT, type ChatAttachedImage } from "../../lib/chatAttachments";
import { useSlashCommandPalette } from "../../hooks/useSlashCommandPalette";
import type { MarcusSlashCommand } from "../../lib/marcusSlashCommands";
import { CompanionInputContextChip } from "./CompanionInputContextChip";
import { CompanionVoiceActiveDisplay } from "./CompanionVoiceActiveDisplay";
import { SlashCommandPalette } from "./SlashCommandPalette";

export type { ChatAttachedImage } from "../../lib/chatAttachments";

export interface CompanionInputProps {
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop?: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onToggleListen?: () => void;
  onCancelListen?: () => void;
  isLoading: boolean;
  isListening?: boolean;
  isVoiceProcessing?: boolean;
  isSecureMicContext?: boolean;
  micHint?: string | null;
  showMicButton?: boolean;
  showContextPicker?: boolean;
  compact?: boolean;
  placeholder?: string;
  attachedImages?: ChatAttachedImage[];
  onImageAttach?: (files: FileList) => void;
  onRemoveImage?: (url: string) => void;
  isUploadingImage?: boolean;
  attachmentError?: string | null;
  queuedMessageCount?: number;
  /**
   * When supplied, typing a leading `/` opens a palette listing these
   * commands. Selecting one calls `onSlashCommandSelect` (the parent owns
   * input clearing + the actual chat send so it can wire in the hidden
   * prompt + display bubble).
   */
  slashCommands?: readonly MarcusSlashCommand[];
  onSlashCommandSelect?: (cmd: MarcusSlashCommand) => void;
}

export function CompanionInput({
  inputRef,
  inputValue,
  onInputChange,
  onSend,
  onStop,
  onKeyDown,
  onToggleListen,
  onCancelListen,
  isLoading,
  isListening = false,
  isVoiceProcessing = false,
  isSecureMicContext = false,
  micHint,
  showMicButton = true,
  showContextPicker = true,
  compact = false,
  placeholder,
  attachedImages,
  onImageAttach,
  onRemoveImage,
  isUploadingImage,
  attachmentError,
  queuedMessageCount = 0,
  slashCommands,
  onSlashCommandSelect,
}: CompanionInputProps) {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef ?? internalRef;
  useAutoResizeTextarea(textareaRef, inputValue, compact ? 160 : 200);

  const slashPalette = useSlashCommandPalette({
    inputValue,
    commands: slashCommands,
    onSelect: onSlashCommandSelect,
  });

  const attachedWidgetContexts = useDepartmentStore((s) => s.attachedWidgetContexts);
  const pickMode = usePickMode();
  const pickModeAvailable = usePickModeAvailable();
  const togglePickMode = useTogglePickMode();

  // Briefly flash the input shell when a new context chip is added so the
  // user sees the attachment confirmed even if the modal opens on top.
  const [justAttached, setJustAttached] = useState(false);
  const [pendingVoiceSubmit, setPendingVoiceSubmit] = useState<string | null>(null);
  const [voiceElapsedSeconds, setVoiceElapsedSeconds] = useState(0);
  const prevContextCountRef = useRef(attachedWidgetContexts.length);
  const fallbackSpeech = useSpeechRecognition({
    onTranscriptReady: (text: string) => {
      const cleanText = text.trim();
      if (!cleanText) return;
      setPendingVoiceSubmit(cleanText);
      onInputChange(cleanText);
    },
    interruptAssistantPlayback: () => {},
  });
  useEffect(() => {
    const prev = prevContextCountRef.current;
    const next = attachedWidgetContexts.length;
    prevContextCountRef.current = next;
    if (next > prev) {
      setJustAttached(true);
      const t = setTimeout(() => setJustAttached(false), 900);
      return () => clearTimeout(t);
    }
  }, [attachedWidgetContexts.length]);
  const shellClass = compact
    ? "relative p-3 bg-card/80 backdrop-blur-[16px] shrink-0 no-drag"
    : "p-4 bg-card/95 backdrop-blur-md border-t border-border/50 shrink-0 no-drag relative z-10 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]";
  const fieldWrapperClass = compact
    ? "flex items-end gap-2 bg-surface-container-low rounded-2xl px-3 py-1.5 shadow-inner transition-colors focus-within:ring-2 focus-within:ring-primary"
    : "flex items-end gap-2 bg-muted/40 rounded-[24px] px-3 py-2 border border-border/50 focus-within:border-primary/50 focus-within:bg-background shadow-inner transition-colors";
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isDropTargetActive, setIsDropTargetActive] = useState(false);

  const hasContent = inputValue.trim().length > 0 || (attachedImages && attachedImages.length > 0);
  const hasQueuedMessages = queuedMessageCount > 0;
  const useExternalMic = typeof onToggleListen === "function";
  const shouldRenderMic = showMicButton;
  const effectiveToggleListen = useExternalMic ? onToggleListen : fallbackSpeech.toggleListen;
  const effectiveCancelListen = useExternalMic ? onCancelListen : fallbackSpeech.stopListening;
  const effectiveIsListening = useExternalMic ? isListening : fallbackSpeech.isListening;
  const effectiveIsVoiceProcessing = useExternalMic
    ? isVoiceProcessing
    : fallbackSpeech.isProcessingVoice;
  const effectiveIsSecureMicContext = useExternalMic
    ? isSecureMicContext
    : fallbackSpeech.isSecureMicContext;
  const effectiveMicHint = useExternalMic ? micHint : fallbackSpeech.micHint;
  const isVoiceActive = shouldRenderMic && (effectiveIsListening || effectiveIsVoiceProcessing);
  const visibleMicHint = shouldRenderMic && !isVoiceActive ? effectiveMicHint : null;
  const canSendVoice = effectiveIsListening && !effectiveIsVoiceProcessing;

  useEffect(() => {
    if (!effectiveIsListening) {
      if (!effectiveIsVoiceProcessing) setVoiceElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setVoiceElapsedSeconds(0);
    const t = setInterval(() => {
      setVoiceElapsedSeconds(Math.max(1, Math.floor((Date.now() - startedAt) / 1000)));
    }, 250);
    return () => clearInterval(t);
  }, [effectiveIsListening, effectiveIsVoiceProcessing]);

  useEffect(() => {
    if (!pendingVoiceSubmit) return;
    if (inputValue.trim() !== pendingVoiceSubmit) return;
    setPendingVoiceSubmit(null);
    onSend();
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, [inputValue, onSend, pendingVoiceSubmit, textareaRef]);

  useEffect(() => {
    if (!canSendVoice) return;

    const handleVoiceKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const isPlainEnter =
        event.key === "Enter" &&
        !(event.shiftKey || event.metaKey || event.ctrlKey || event.altKey);
      if (isPlainEnter) {
        event.preventDefault();
        effectiveToggleListen?.();
        return;
      }
      if (event.key === "Escape" && effectiveCancelListen) {
        event.preventDefault();
        effectiveCancelListen();
      }
    };

    window.addEventListener("keydown", handleVoiceKeyDown);
    return () => window.removeEventListener("keydown", handleVoiceKeyDown);
  }, [canSendVoice, effectiveCancelListen, effectiveToggleListen]);

  const primaryActionDisabled = !hasContent;

  const handlePrimaryAction = () => {
    onSend();
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  };

  const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashPalette.handleKeyDown(event)) return;
    onKeyDown(event);
  };

  return (
    <div
      className={`${shellClass} transition-[box-shadow,background-color] duration-300 ${
        justAttached
          ? "bg-cyan-500/[0.08] shadow-[0_0_0_1px_rgba(34,211,238,0.35),0_0_24px_rgba(34,211,238,0.18)]"
          : ""
      } ${isDropTargetActive ? "ring-2 ring-primary/50 bg-primary/5" : ""}`}
      onDragEnter={(e) => {
        if (!onImageAttach) return;
        const hasFileItem =
          Array.from(e.dataTransfer.items || []).some((item) => item.kind === "file") ||
          Array.from(e.dataTransfer.types || []).includes("Files");
        if (!hasFileItem) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDropTargetActive(true);
      }}
      onDragOver={(e) => {
        if (!onImageAttach) return;
        const hasFileItem =
          Array.from(e.dataTransfer.items || []).some((item) => item.kind === "file") ||
          Array.from(e.dataTransfer.types || []).includes("Files");
        if (!hasFileItem) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        if (!isDropTargetActive) setIsDropTargetActive(true);
      }}
      onDragLeave={(e) => {
        if (!onImageAttach) return;
        const nextTarget = e.relatedTarget as Node | null;
        if (nextTarget && e.currentTarget.contains(nextTarget)) return;
        setIsDropTargetActive(false);
      }}
      onDrop={(e) => {
        if (!onImageAttach) return;
        const droppedFiles = Array.from(e.dataTransfer.files || []);
        e.preventDefault();
        e.stopPropagation();
        setIsDropTargetActive(false);
        if (droppedFiles.length === 0) return;
        const dt = new DataTransfer();
        droppedFiles.forEach((file) => dt.items.add(file));
        onImageAttach(dt.files);
      }}
    >
      {isDropTargetActive && onImageAttach && (
        <div className="pointer-events-none absolute inset-1 z-20 rounded-2xl border-2 border-dashed border-primary/60 bg-primary/10 backdrop-blur-[2px] flex items-center justify-center text-xs font-semibold text-primary">
          Drop images or videos to attach
        </div>
      )}

      {showContextPicker && attachedWidgetContexts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2 px-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-cyan-400/80">
            Context
          </span>
          {attachedWidgetContexts.map((ctx, i) => (
            <CompanionInputContextChip key={ctx.widgetId} ctx={ctx} index={i} />
          ))}
        </div>
      )}

      {/* Attached media previews */}
      {attachedImages && attachedImages.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2 px-1">
          {attachedImages.map((img) => (
            <div
              key={img.url}
              className="relative group w-12 h-12 rounded-lg overflow-hidden border border-border/50 bg-muted/30"
            >
              {img.type === "video" ? (
                <video
                  src={img.url}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img src={img.url} alt={img.filename} className="w-full h-full object-cover" />
              )}
              {onRemoveImage && (
                <button
                  type="button"
                  onClick={() => onRemoveImage(img.url)}
                  className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
          {isUploadingImage && (
            <div className="flex items-center justify-center w-12 h-12 rounded-lg border border-border/50 bg-muted/30">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      <div className={`${fieldWrapperClass} relative`} id="marcus-slash-command-palette">
        {slashCommands && slashCommands.length > 0 && (
          <SlashCommandPalette
            open={slashPalette.isOpen}
            commands={slashPalette.filteredCommands}
            activeIndex={slashPalette.activeIndex}
            compact={compact}
            showCategories={slashPalette.showCategories}
            onSelect={slashPalette.selectCommand}
            onHover={slashPalette.setActiveIndex}
          />
        )}
        {/* Action buttons — grouped tightly */}
        <div className={`flex items-center ${compact ? "gap-0.5" : "gap-1"} shrink-0`}>
          {shouldRenderMic && !isVoiceActive && (
            <button
              type="button"
              onClick={effectiveToggleListen}
              disabled={isLoading || !effectiveIsSecureMicContext}
              aria-label="Record voice message"
              title={
                !effectiveIsSecureMicContext
                  ? "Microphone requires HTTPS or localhost"
                  : "Record voice message"
              }
              className={`${compact ? "h-8 w-8" : "h-10 w-10"} inline-flex items-center justify-center rounded-full shrink-0 transition-all focus:outline-none bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary disabled:opacity-40 disabled:hover:bg-primary/10`}
            >
              <Mic className={compact ? "w-4 h-4" : "w-[18px] h-[18px]"} />
            </button>
          )}

          {isVoiceActive && !effectiveIsVoiceProcessing && effectiveCancelListen && (
            <button
              type="button"
              onClick={effectiveCancelListen}
              aria-label="Cancel voice message"
              title="Cancel voice message"
              className={`${compact ? "h-8 w-8" : "h-10 w-10"} inline-flex items-center justify-center rounded-full shrink-0 bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground transition-all focus:outline-none`}
            >
              <X className={compact ? "w-4 h-4" : "w-[18px] h-[18px]"} />
            </button>
          )}

          {canSendVoice && (
            <button
              type="button"
              onClick={effectiveToggleListen}
              aria-label="Send voice message"
              title="Send voice message"
              className={`${compact ? "h-8 w-8" : "h-10 w-10"} inline-flex items-center justify-center rounded-full shrink-0 bg-primary text-primary-foreground shadow-md hover:bg-primary/90 transition-all focus:outline-none`}
            >
              <Send className={`${compact ? "w-4 h-4" : "w-[18px] h-[18px] ml-0.5"}`} />
            </button>
          )}

          {showContextPicker && pickModeAvailable && (
            <button
              type="button"
              onClick={togglePickMode}
              title={pickMode ? "Cancel context pick" : "Attach widget context"}
              className={`${compact ? "p-1.5" : "p-2.5"} rounded-lg shrink-0 transition-all focus:outline-none ${
                pickMode
                  ? "bg-cyan-500/20 text-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.25)] ring-1 ring-cyan-500/40"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              }`}
            >
              <Crosshair
                className={`${compact ? "w-4 h-4" : "w-[18px] h-[18px]"} ${pickMode ? "animate-pulse" : ""}`}
              />
            </button>
          )}

          {onImageAttach && (
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={isUploadingImage}
              title="Attach media"
              className={`${compact ? "p-1.5" : "p-2.5"} rounded-lg shrink-0 transition-all focus:outline-none text-muted-foreground hover:bg-muted/60 hover:text-foreground disabled:opacity-40`}
            >
              {isUploadingImage && !attachedImages?.length ? (
                <Loader2 className={`${compact ? "w-4 h-4" : "w-[18px] h-[18px]"} animate-spin`} />
              ) : (
                <ImagePlus className={compact ? "w-4 h-4" : "w-[18px] h-[18px]"} />
              )}
            </button>
          )}
        </div>

        {onImageAttach && (
          <input
            ref={imageInputRef}
            type="file"
            accept={CHAT_MEDIA_UPLOAD_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) {
                onImageAttach(e.target.files);
                e.target.value = "";
              }
            }}
          />
        )}

        {isVoiceActive ? (
          <CompanionVoiceActiveDisplay
            compact={compact}
            isProcessing={effectiveIsVoiceProcessing}
            isListening={effectiveIsListening}
            elapsedSeconds={voiceElapsedSeconds}
          />
        ) : (
          <textarea
            ref={textareaRef}
            dir="auto"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            role="combobox"
            aria-expanded={slashPalette.isOpen}
            aria-autocomplete={slashCommands && slashCommands.length > 0 ? "list" : undefined}
            aria-controls={slashPalette.isOpen ? "marcus-slash-command-palette" : undefined}
            onPaste={(e) => {
              if (!onImageAttach) return;
              const items = e.clipboardData?.items;
              if (!items) return;
              const imageFiles: File[] = [];
              for (const item of Array.from(items)) {
                if (item.type.startsWith("image/")) {
                  const file = item.getAsFile();
                  if (file) imageFiles.push(file);
                }
              }
              if (imageFiles.length > 0) {
                e.preventDefault();
                const dt = new DataTransfer();
                imageFiles.forEach((f) => dt.items.add(f));
                onImageAttach(dt.files);
              }
            }}
            placeholder={placeholder ?? "Message..."}
            rows={1}
            className={`flex-1 bg-transparent ${compact ? "text-sm" : "text-[15px]"} text-foreground placeholder:text-muted-foreground outline-none resize-none ${compact ? "max-h-24 py-2" : "max-h-24 sm:max-h-32 py-3"} ds-scrollbar min-w-0 font-medium ${detectRtl(inputValue) ? "font-[Vazirmatn] text-right" : ""}`}
          />
        )}

        {!isVoiceActive && isLoading && onStop && (
          <button
            type="button"
            onClick={() => {
              onStop();
              requestAnimationFrame(() => {
                textareaRef.current?.focus();
              });
            }}
            title="Stop response"
            className={`${compact ? "p-2" : "p-2.5 mb-0.5"} rounded-full shrink-0 bg-destructive/12 text-destructive shadow-sm hover:bg-destructive hover:text-destructive-foreground transition-all focus:outline-none`}
          >
            <Square className={`${compact ? "w-3.5 h-3.5" : "w-[18px] h-[18px]"}`} />
          </button>
        )}

        {!isVoiceActive && (
          <button
            type="button"
            onClick={handlePrimaryAction}
            disabled={primaryActionDisabled}
            title={isLoading ? "Queue message" : "Send message"}
            aria-label="Send message"
            className={`${compact ? "p-2" : "p-2.5 mb-0.5"} rounded-full shrink-0 bg-primary text-primary-foreground shadow-md hover:bg-primary/90 disabled:opacity-30 disabled:hover:bg-primary transition-all disabled:shadow-none focus:outline-none`}
          >
            <Send className={`${compact ? "w-3.5 h-3.5" : "w-[18px] h-[18px] ml-0.5"}`} />
          </button>
        )}
      </div>
      {hasQueuedMessages && (
        <p className={`mt-2 text-primary ${compact ? "text-[11px]" : "text-xs"}`}>
          {queuedMessageCount} queued
        </p>
      )}
      {visibleMicHint && (
        <p className={`mt-2 text-amber-500 ${compact ? "text-[11px]" : "text-xs"}`}>
          {visibleMicHint}
        </p>
      )}
      {attachmentError && (
        <p className={`mt-2 text-destructive ${compact ? "text-[11px]" : "text-xs"}`}>
          {attachmentError}
        </p>
      )}
    </div>
  );
}
